import React, { useState, useEffect } from 'react';
import { ChevronDown, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { useToast } from '../../../components/common/FeedbackSystem';
import referralService, { Referral } from '../../../lib/referralService';

/**
 * ReferralsManagement - Composant pour gérer les redirections d'emails
 */
const ReferralsManagement: React.FC = () => {
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'pending' | 'contacted' | 'converted'>('pending');
  const [expandedReferral, setExpandedReferral] = useState<string | null>(null);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const { showSuccess, showError } = useToast();

  // Charger les redirections
  useEffect(() => {
    loadReferrals();
  }, [activeTab]);

  // Fonction pour charger les redirections selon le statut actif
  const loadReferrals = async () => {
    setIsLoading(true);
    try {
      const data = await referralService.getReferralsByStatus(activeTab);
      setReferrals(data);
    } catch (err) {
      console.error('Erreur lors du chargement des redirections:', err);
      showError('Erreur', 'Impossible de charger les redirections.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour mettre à jour le statut d'une redirection
  const updateStatus = async (id: string, newStatus: 'pending' | 'contacted' | 'converted', notes?: string) => {
    setProcessingIds(prev => new Set([...prev, id]));
    try {
      await referralService.updateReferralStatus(id, newStatus, notes);
      showSuccess('Succès', 'Statut de la redirection mis à jour avec succès!');
      loadReferrals();
    } catch (err) {
      console.error('Erreur lors de la mise à jour du statut:', err);
      showError('Erreur', 'Impossible de mettre à jour le statut.');
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set([...prev]);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  // Cette fonction pourrait être utilisée pour afficher plus de détails sur le prospect si nécessaire
  // Pour l'instant, nous utilisons uniquement les informations de redirection

  // Gestion de l'expansion des détails
  const toggleReferralDetails = async (id: string) => {
    if (expandedReferral === id) {
      setExpandedReferral(null);
    } else {
      setExpandedReferral(id);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <h2 className="text-2xl font-semibold mb-4">Gestion des redirections d'emails</h2>
      <p className="text-gray-600 mb-6">
        Cette page vous permet de gérer les redirections d'emails collectées via les formulaires de redirection.
      </p>

      {/* Onglets pour filtrer par statut */}
      <div className="flex border-b mb-6">
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'pending'
              ? 'text-amber-500 border-b-2 border-amber-500'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          En attente
        </button>
        <button
          onClick={() => setActiveTab('contacted')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'contacted'
              ? 'text-amber-500 border-b-2 border-amber-500'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Contactés
        </button>
        <button
          onClick={() => setActiveTab('converted')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'converted'
              ? 'text-amber-500 border-b-2 border-amber-500'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Convertis
        </button>
      </div>

      {/* Liste des redirections */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center py-8">
            <div className="inline-block w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mb-2"></div>
            <p className="text-gray-600">Chargement des redirections...</p>
          </div>
        ) : referrals.length === 0 ? (
          <div className="text-center py-8 border rounded-lg">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-600">Aucune redirection avec ce statut.</p>
          </div>
        ) : (
          referrals.map((referral) => (
            <div 
              key={referral.id} 
              className="border rounded-lg overflow-hidden"
            >
              <div 
                className="p-4 bg-gray-50 cursor-pointer flex justify-between items-center"
                onClick={() => toggleReferralDetails(referral.id)}
              >
                <div>
                  <div className="font-medium">{referral.referred_email}</div>
                  <div className="text-sm text-gray-600">
                    {new Date(referral.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center">
                  {activeTab === 'pending' && !processingIds.has(referral.id) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        updateStatus(referral.id, 'contacted');
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm mr-4"
                    >
                      Marquer comme contacté
                    </button>
                  )}
                  {activeTab === 'contacted' && !processingIds.has(referral.id) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        updateStatus(referral.id, 'converted');
                      }}
                      className="text-green-600 hover:text-green-800 text-sm mr-4"
                    >
                      Marquer comme converti
                    </button>
                  )}
                  <ChevronDown 
                    className={`h-5 w-5 transition-transform ${
                      expandedReferral === referral.id ? 'transform rotate-180' : ''
                    }`} 
                  />
                </div>
              </div>
              
              {/* Détails de la redirection */}
              {expandedReferral === referral.id && (
                <div className="p-4 border-t">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold mb-2">Détails de la redirection</h4>
                      <p><span className="font-medium">Email référé:</span> {referral.referred_email}</p>
                      <p><span className="font-medium">Email original:</span> {referral.original_email || 'N/A'}</p>
                      <p>
                        <span className="font-medium">Statut:</span> 
                        {referral.status === 'pending' && (
                          <span className="inline-flex items-center text-amber-600 ml-1">
                            <Clock className="h-4 w-4 mr-1" /> En attente
                          </span>
                        )}
                        {referral.status === 'contacted' && (
                          <span className="inline-flex items-center text-blue-600 ml-1">
                            <CheckCircle className="h-4 w-4 mr-1" /> Contacté
                          </span>
                        )}
                        {referral.status === 'converted' && (
                          <span className="inline-flex items-center text-green-600 ml-1">
                            <CheckCircle className="h-4 w-4 mr-1" /> Converti
                          </span>
                        )}
                      </p>
                      <p><span className="font-medium">Date de création:</span> {new Date(referral.created_at).toLocaleString()}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Notes</h4>
                      <textarea
                        className="w-full border rounded-md p-2 h-24"
                        value={referral.notes || ''}
                        onChange={(e) => {
                          // Mise à jour locale des notes
                          setReferrals(prev => 
                            prev.map(r => r.id === referral.id ? { ...r, notes: e.target.value } : r)
                          );
                        }}
                        placeholder="Ajoutez des notes concernant cette redirection..."
                      ></textarea>
                      <button
                        onClick={() => {
                          const currentReferral = referrals.find(r => r.id === referral.id);
                          if (currentReferral) {
                            updateStatus(referral.id, currentReferral.status as any, currentReferral.notes);
                          }
                        }}
                        className="mt-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-1 px-3 rounded text-sm"
                        disabled={processingIds.has(referral.id)}
                      >
                        {processingIds.has(referral.id) ? 'Enregistrement...' : 'Enregistrer les notes'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ReferralsManagement;
